import { Table } from 'antd'
import type { TableColumnsType } from 'antd'
import { createElement } from 'react'
import { ComponentController } from './component_controller'
import { visit } from '@hotwired/turbo'

interface DataType {
  key: string
  [key: string]: any
}

interface Props {
  columns: Array<{ title: string; key: string }>
  data: Array<DataType>
  currentUrl?: string
  sortColumn?: string
  sortDirection?: 'asc' | 'desc'
  selectable?: boolean
  turboSafe?: boolean
  useTurboForSorting?: boolean
  turboFrameId?: string
}

const AntTable = ({
  columns,
  data,
  currentUrl,
  sortColumn,
  sortDirection,
  selectable,
  turboSafe = false,
  useTurboForSorting = false,
  turboFrameId = '',
}: Props) => {
  const lastColumnIndex = columns.length - 1
  const antColumns: TableColumnsType<DataType> = columns.map(
    (
      col: { title: string; key: string; sort: boolean; hidden: boolean },
      index: number,
    ) => ({
      title: col.hidden ? '' : col.title,
      dataIndex: col.key,
      key: col.key,
      sorter: col.sort,
      sortOrder:
        sortColumn === col.key
          ? sortDirection === 'asc'
            ? 'ascend'
            : 'descend'
          : undefined,
      className: sortColumn === col.key ? 'highlight' : '',
      fixed: index === lastColumnIndex ? 'right' : undefined,
      onHeaderCell: () => ({
        'aria-label': col.title,
      }),
      render: (text: string) => {
        if (turboSafe) {
          return createElement('div', {}, text)
        } else {
          return createElement('div', {
            dangerouslySetInnerHTML: { __html: text },
          })
        }
      },
    }),
  )

  const handleChange = (_: any, __: any, sorter: any) => {
    const direction = sorter.order === 'ascend' ? 'asc' : 'desc'
    const url = new URL(currentUrl, window.location.origin)

    if (currentUrl && sorter.column) {
      url.searchParams.set('sort', sorter.column.key)
      url.searchParams.set('direction', direction)
    } else {
      url.searchParams.delete('sort')
      url.searchParams.delete('direction')
    }

    if (useTurboForSorting && turboFrameId) {
      visit(url.toString(), { frame: turboFrameId })
    } else {
      window.location.href = url.toString()
    }
  }

  const tableProps = {
    bordered: true,
    columns: antColumns,
    dataSource: data,
    scroll: { x: true },
    pagination: false,
    onChange: handleChange,
    rowSelection: selectable
      ? {
          onChange: (
            selectedRowKeys: React.Key[],
            selectedRows: DataType[],
          ) => {
            document.body.setAttribute(
              'data-selected-table-ids',
              JSON.stringify(selectedRowKeys),
            )

            document.dispatchEvent(
              new CustomEvent('selectionChanged', {
                detail: { selectedIds: selectedRowKeys },
              }),
            )
          },
        }
      : undefined,
  } as const

  return createElement(Table<DataType>, tableProps)
}

export class AntTableController extends ComponentController {
  get component() {
    return AntTable
  }
}
